import { useState, useEffect, useMemo } from "react";
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfDay,
  endOfDay,
  addDays,
  addWeeks,
  subWeeks,
  isSameDay,
  parseISO,
} from "date-fns";
import {
  getAssignments,
  getShifts,
  getProjects,
  getStaffList,
  extractErrorMessage,
} from "../lib/api";
import { LoadingSpinner } from "../components/shared/DataComponents";
import { useFormatters } from "../components/shared/useFormatters";
import { 
  useCalendarEdit, 
  CalendarEditControls, 
  CalendarEditModeBanner,
  CalendarEditLegend,
  EditableShiftBlock 
} from "../components/calendar";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  AlertTriangle,
  Moon,
  AlertCircle,
  CheckCircle2,
  Clock,
  User,
} from "lucide-react";
import { cn } from "../lib/utils";

// Generate hours for the timeline (6am to 10pm)
const HOURS = Array.from({ length: 17 }, (_, i) => i + 6);
const HOUR_HEIGHT = 50; // pixels per hour
const ROW_HEIGHT = 70; // pixels per staff row

// Color palette for shifts
const SHIFT_COLORS = [
  { bg: "bg-teal-500/30", border: "border-teal-500", text: "text-teal-400" },
  { bg: "bg-blue-500/30", border: "border-blue-500", text: "text-blue-400" },
  { bg: "bg-purple-500/30", border: "border-purple-500", text: "text-purple-400" },
  { bg: "bg-amber-500/30", border: "border-amber-500", text: "text-amber-400" },
  { bg: "bg-rose-500/30", border: "border-rose-500", text: "text-rose-400" },
  { bg: "bg-emerald-500/30", border: "border-emerald-500", text: "text-emerald-400" },
];

// Status colors
const STATUS_STYLES = {
  pending: { bg: "bg-amber-500/20", border: "border-amber-500", icon: Clock },
  confirmed: { bg: "bg-emerald-500/20", border: "border-emerald-500", icon: CheckCircle2 },
  completed: { bg: "bg-blue-500/20", border: "border-blue-500", icon: CheckCircle2 },
  declined: { bg: "bg-red-500/20", border: "border-red-500", icon: AlertCircle },
  no_show: { bg: "bg-red-500/20", border: "border-red-500", icon: AlertCircle },
};

export default function StaffCalendarPage() {
  const { formatDate, formatTime, formatDuration } = useFormatters();
  const { isEditMode, registerOnSaveSuccess, getPendingChange } = useCalendarEdit();
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [staff, setStaff] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("week"); // "day" | "week"
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Load data function - reusable for refresh
  const loadData = async () => {
    try {
      const [assignmentsRes, shiftsRes, projectsRes, staffRes] = await Promise.all([
        getAssignments({ limit: 1000 }),
        getShifts({ limit: 500 }),
        getProjects({ limit: 100 }),
        getStaffList({ limit: 500 }),
      ]);
      setAssignments(assignmentsRes.data || []);
      setShifts(shiftsRes.data || []);
      setProjects(projectsRes.data || []);
      // Handle paginated response format
      const staffData = staffRes.data;
      setStaff(staffData?.items || staffData || []);
    } catch (err) {
      console.error("Failed to load calendar data:", extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadData();
  }, []);

  // Register refresh callback for save success
  useEffect(() => {
    registerOnSaveSuccess(loadData);
  }, [registerOnSaveSuccess]);

  // Create lookup maps
  const shiftsMap = useMemo(() => {
    const map = new Map();
    shifts.forEach((shift) => map.set(shift.id, shift));
    return map;
  }, [shifts]);

  const projectsMap = useMemo(() => {
    const map = new Map();
    projects.forEach((project) => map.set(project.id, project));
    return map;
  }, [projects]);

  const staffMap = useMemo(() => {
    const map = new Map();
    staff.forEach((s) => map.set(s.id, s));
    return map;
  }, [staff]);

  // Calculate date range
  const dateRange = useMemo(() => {
    if (viewMode === "day") {
      return {
        start: startOfDay(currentDate),
        end: endOfDay(currentDate),
        days: [currentDate],
      };
    } else {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
      return {
        start,
        end: endOfWeek(currentDate, { weekStartsOn: 1 }),
        days,
      };
    }
  }, [currentDate, viewMode]);

  // Get assignments with shift data for the date range
  const enrichedAssignments = useMemo(() => {
    return assignments
      .map((assignment) => {
        const shift = shiftsMap.get(assignment.shift_id);
        if (!shift) return null;
        
        const project = projectsMap.get(shift.project_id);
        const staffMember = staffMap.get(assignment.staff_id);
        
        return {
          ...assignment,
          shift,
          project,
          staffMember,
        };
      })
      .filter((a) => a && a.shift?.date);
  }, [assignments, shiftsMap, projectsMap, staffMap]);

  // Filter assignments for current date range
  const filteredAssignments = useMemo(() => {
    return enrichedAssignments.filter((assignment) => {
      const shiftDate = parseISO(assignment.shift.date);
      return dateRange.days.some((day) => isSameDay(shiftDate, day));
    });
  }, [enrichedAssignments, dateRange]);

  // Get unique staff with assignments in date range
  const staffWithAssignments = useMemo(() => {
    const staffIds = new Set(filteredAssignments.map((a) => a.staff_id));
    return staff.filter((s) => staffIds.has(s.id));
  }, [filteredAssignments, staff]);

  // Group assignments by staff
  const assignmentsByStaff = useMemo(() => {
    const grouped = new Map();
    staffWithAssignments.forEach((s) => {
      grouped.set(s.id, []);
    });
    
    filteredAssignments.forEach((assignment) => {
      const staffAssignments = grouped.get(assignment.staff_id);
      if (staffAssignments) {
        staffAssignments.push(assignment);
      }
    });
    
    return grouped;
  }, [filteredAssignments, staffWithAssignments]);

  // Detect overlapping assignments for a crew member
  const detectOverlaps = (staffId) => {
    const staffAssignments = assignmentsByStaff.get(staffId) || [];
    const overlaps = new Set();
    
    for (let i = 0; i < staffAssignments.length; i++) {
      for (let j = i + 1; j < staffAssignments.length; j++) {
        const a1 = staffAssignments[i];
        const a2 = staffAssignments[j];
        
        // Same day check
        if (a1.shift.date !== a2.shift.date) continue;
        
        // Parse times
        const [s1h, s1m] = a1.shift.start_time.split(":").map(Number);
        const [e1h, e1m] = a1.shift.end_time.split(":").map(Number);
        const [s2h, s2m] = a2.shift.start_time.split(":").map(Number);
        const [e2h, e2m] = a2.shift.end_time.split(":").map(Number);
        
        const start1 = s1h * 60 + s1m;
        const end1 = e1h * 60 + e1m + (e1h < s1h ? 24 * 60 : 0);
        const start2 = s2h * 60 + s2m;
        const end2 = e2h * 60 + e2m + (e2h < s2h ? 24 * 60 : 0);
        
        // Check overlap
        if (start1 < end2 && start2 < end1) {
          overlaps.add(a1.id);
          overlaps.add(a2.id);
        }
      }
    }
    
    return overlaps;
  };

  // Navigation handlers
  const goToPrevious = () => {
    if (viewMode === "day") {
      setCurrentDate(addDays(currentDate, -1));
    } else {
      setCurrentDate(subWeeks(currentDate, 1));
    }
  };

  const goToNext = () => {
    if (viewMode === "day") {
      setCurrentDate(addDays(currentDate, 1));
    } else {
      setCurrentDate(addWeeks(currentDate, 1));
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Calculate assignment position
  const getAssignmentStyle = (shift) => {
    const [startHour, startMin] = (shift.start_time || "09:00").split(":").map(Number);
    const [endHour, endMin] = (shift.end_time || "17:00").split(":").map(Number);
    
    let adjustedEndHour = endHour;
    let adjustedEndMin = endMin;
    if (endHour < startHour) {
      adjustedEndHour = 22;
      adjustedEndMin = 0;
    }
    
    const startOffset = (startHour - 6) * HOUR_HEIGHT + (startMin / 60) * HOUR_HEIGHT;
    const durationMinutes = (adjustedEndHour - startHour) * 60 + (adjustedEndMin - startMin);
    const width = Math.max((durationMinutes / 60) * HOUR_HEIGHT, 40);
    
    return {
      left: `${Math.max(startOffset, 0)}px`,
      width: `${width}px`,
    };
  };

  // Check if shift is long
  const isLongShift = (shift) => {
    return shift.duration_hours && shift.duration_hours > 8;
  };

  // Open assignment detail
  const handleAssignmentClick = (assignment) => {
    setSelectedAssignment(assignment);
    setDetailOpen(true);
  };

  // Get staff initials
  const getStaffInitials = (staffMember) => {
    if (!staffMember) return "??";
    const first = staffMember.first_name?.[0] || "";
    const last = staffMember.last_name?.[0] || "";
    return `${first}${last}`.toUpperCase() || "??";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-secondary tracking-tight">Crew Assignments Calendar</h1>
          <p className="text-sm text-muted-foreground mt-1">
            View crew assignments and detect scheduling conflicts
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Edit Mode Toggle */}
          <CalendarEditControls />
          
          <Select value={viewMode} onValueChange={setViewMode}>
            <SelectTrigger className="w-28" data-testid="staff-calendar-view-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Day</SelectItem>
              <SelectItem value="week">Week</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" size="sm" onClick={goToToday} data-testid="staff-calendar-today-btn">
            Today
          </Button>
        </div>
      </div>

      {/* Edit Mode Banner */}
      <CalendarEditModeBanner />

      {/* Navigation */}
      <div className="flex items-center justify-between bg-card/50 rounded-lg p-3 border border-white/10">
        <Button variant="ghost" size="icon" onClick={goToPrevious} data-testid="staff-calendar-prev-btn">
          <ChevronLeft className="h-5 w-5" />
        </Button>
        
        <div className="text-center">
          <h2 className="text-lg font-semibold font-secondary" data-testid="staff-calendar-date-range">
            {viewMode === "day"
              ? format(currentDate, "EEEE, MMMM d, yyyy")
              : `${format(dateRange.start, "MMM d")} - ${format(dateRange.end, "MMM d, yyyy")}`}
          </h2>
        </div>
        
        <Button variant="ghost" size="icon" onClick={goToNext} data-testid="staff-calendar-next-btn">
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs">
        <div className="flex items-center gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
          <span className="text-muted-foreground">Overlap Conflict</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Moon className="h-3.5 w-3.5 text-purple-500" />
          <span className="text-muted-foreground">Long Shift (&gt;8h)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
          <span className="text-muted-foreground">Confirmed</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5 text-amber-500" />
          <span className="text-muted-foreground">Pending</span>
        </div>
        {/* Edit mode legend */}
        <CalendarEditLegend />
      </div>

      {/* Crew Count */}
      <div className="text-sm text-muted-foreground">
        Showing {staffWithAssignments.length} crew members with assignments
      </div>

      {/* Calendar Grid */}
      <div className="bg-card/30 rounded-lg border border-white/10 overflow-hidden">
        {staffWithAssignments.length === 0 ? (
          <div className="p-12 text-center">
            <User className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No crew assignments found for this period</p>
          </div>
        ) : (
          <>
            {/* Time Header */}
            <div className="flex border-b border-white/10 bg-card/50">
              <div className="w-40 shrink-0 p-2 border-r border-white/10">
                <span className="text-xs text-muted-foreground uppercase font-medium">Crew</span>
              </div>
              <div className="flex-1 overflow-x-auto">
                <div className="flex" style={{ width: `${HOURS.length * HOUR_HEIGHT}px` }}>
                  {HOURS.map((hour) => (
                    <div
                      key={hour}
                      className="text-xs text-muted-foreground text-center border-l border-white/5 py-2"
                      style={{ width: `${HOUR_HEIGHT}px` }}
                    >
                      {format(new Date().setHours(hour, 0), "ha")}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Crew Rows */}
            <ScrollArea className="h-[500px]">
              {viewMode === "day" ? (
                // Day view - single timeline per staff
                staffWithAssignments.map((staffMember) => {
                  const staffAssignments = (assignmentsByStaff.get(staffMember.id) || [])
                    .filter(a => isSameDay(parseISO(a.shift.date), currentDate));
                  const overlaps = detectOverlaps(staffMember.id);
                  
                  return (
                    <div key={staffMember.id} className="flex border-b border-white/5 hover:bg-white/5">
                      {/* Crew Info */}
                      <div className="w-40 shrink-0 p-2 border-r border-white/10 flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="text-xs bg-primary/20 text-primary">
                            {getStaffInitials(staffMember)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">
                            {staffMember.first_name} {staffMember.last_name}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {staffAssignments.length} shift{staffAssignments.length !== 1 ? "s" : ""}
                          </p>
                        </div>
                      </div>
                      
                      {/* Timeline */}
                      <div 
                        className="flex-1 relative overflow-x-auto"
                        style={{ height: `${ROW_HEIGHT}px` }}
                      >
                        <div 
                          className="absolute top-0 left-0 h-full"
                          style={{ width: `${HOURS.length * HOUR_HEIGHT}px` }}
                        >
                          {/* Hour lines */}
                          {HOURS.map((hour) => (
                            <div
                              key={hour}
                              className="absolute top-0 h-full border-l border-white/5"
                              style={{ left: `${(hour - 6) * HOUR_HEIGHT}px` }}
                            />
                          ))}
                          
                          {/* Assignments */}
                          {staffAssignments.map((assignment, idx) => {
                            const pendingChange = getPendingChange(assignment.shift.id);
                            const displayShift = pendingChange?.modified || assignment.shift;
                            const style = getAssignmentStyle(displayShift);
                            const hasOverlap = overlaps.has(assignment.id);
                            const long = isLongShift(displayShift);
                            const statusStyle = STATUS_STYLES[assignment.status] || STATUS_STYLES.pending;
                            const colors = SHIFT_COLORS[idx % SHIFT_COLORS.length];
                            
                            return (
                              <EditableShiftBlock
                                key={assignment.id}
                                shiftId={assignment.shift.id}
                                shiftData={assignment.shift}
                                className={cn(
                                  "absolute top-2 h-[calc(100%-16px)] rounded-md border-l-4 px-2 py-1 overflow-hidden",
                                  hasOverlap ? "border-red-500 bg-red-500/20" : colors.border,
                                  hasOverlap ? "" : colors.bg,
                                  !isEditMode && "cursor-pointer hover:scale-[1.02] transition-all"
                                )}
                                style={style}
                                onClick={() => handleAssignmentClick(assignment)}
                                colors={colors}
                                data-testid={`staff-assignment-${assignment.id}`}
                              >
                                <div className="flex items-start justify-between gap-1">
                                  <div className="min-w-0 flex-1">
                                    <p className={cn("text-[10px] font-semibold truncate", colors.text)}>
                                      {assignment.project?.name || "Unknown"}
                                    </p>
                                    <p className={cn(
                                      "text-[9px]",
                                      pendingChange ? "text-amber-400 font-medium" : "text-muted-foreground"
                                    )}>
                                      {displayShift.start_time}-{displayShift.end_time}
                                    </p>
                                  </div>
                                  <div className="flex gap-0.5">
                                    {hasOverlap && (
                                      <AlertTriangle className="h-3 w-3 text-red-500" />
                                    )}
                                    {long && (
                                      <Moon className="h-3 w-3 text-purple-500" />
                                    )}
                                  </div>
                                </div>
                              </EditableShiftBlock>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                // Week view - day columns per staff
                staffWithAssignments.map((staffMember) => {
                  const allStaffAssignments = assignmentsByStaff.get(staffMember.id) || [];
                  const overlaps = detectOverlaps(staffMember.id);
                  
                  return (
                    <div key={staffMember.id} className="border-b border-white/5">
                      {/* Crew Header */}
                      <div className="flex items-center gap-2 p-2 bg-card/30 border-b border-white/5">
                        <Avatar className="h-6 w-6">
                          <AvatarFallback className="text-xs bg-primary/20 text-primary">
                            {getStaffInitials(staffMember)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm font-medium">
                          {staffMember.first_name} {staffMember.last_name}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          ({allStaffAssignments.length} assignments)
                        </span>
                      </div>
                      
                      {/* Day Columns */}
                      <div className="grid grid-cols-7">
                        {dateRange.days.map((day) => {
                          const dayKey = format(day, "yyyy-MM-dd");
                          const dayAssignments = allStaffAssignments.filter(
                            (a) => a.shift.date === dayKey
                          );
                          
                          return (
                            <div
                              key={dayKey}
                              className={cn(
                                "min-h-[60px] p-1 border-l border-white/5 first:border-l-0",
                                isSameDay(day, new Date()) && "bg-primary/5"
                              )}
                            >
                              {dayAssignments.map((assignment, idx) => {
                                const pendingChange = getPendingChange(assignment.shift.id);
                                const displayShift = pendingChange?.modified || assignment.shift;
                                const hasOverlap = overlaps.has(assignment.id);
                                const long = isLongShift(displayShift);
                                const colors = SHIFT_COLORS[idx % SHIFT_COLORS.length];
                                
                                return (
                                  <EditableShiftBlock
                                    key={assignment.id}
                                    shiftId={assignment.shift.id}
                                    shiftData={assignment.shift}
                                    className={cn(
                                      "rounded p-1 mb-1 text-[10px] border-l-2",
                                      hasOverlap ? "border-red-500 bg-red-500/20" : colors.border,
                                      hasOverlap ? "" : colors.bg,
                                      !isEditMode && "cursor-pointer hover:scale-[1.02] transition-all"
                                    )}
                                    onClick={() => handleAssignmentClick(assignment)}
                                    colors={colors}
                                  >
                                    <div className="flex items-center justify-between">
                                      <span className={cn(
                                        "font-medium truncate",
                                        pendingChange && "text-amber-400"
                                      )}>
                                        {displayShift.start_time}
                                      </span>
                                      {hasOverlap && <AlertTriangle className="h-2.5 w-2.5 text-red-500" />}
                                      {long && <Moon className="h-2.5 w-2.5 text-purple-500" />}
                                    </div>
                                    <p className="truncate text-muted-foreground">
                                      {assignment.project?.name?.slice(0, 15)}
                                    </p>
                                  </EditableShiftBlock>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </ScrollArea>
          </>
        )}
      </div>

      {/* Assignment Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="bg-surface-base border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              Assignment Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedAssignment && (
            <div className="space-y-4">
              {/* Staff Info */}
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-primary/20 text-primary">
                    {getStaffInitials(selectedAssignment.staffMember)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    {selectedAssignment.staffMember?.first_name}{" "}
                    {selectedAssignment.staffMember?.last_name}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {selectedAssignment.project?.name || "Unknown Project"}
                  </p>
                </div>
              </div>
              
              {/* Shift Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Shift</p>
                  <p className="text-white font-medium">
                    {selectedAssignment.shift?.name || "Unnamed Shift"}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Status</p>
                  <Badge
                    variant="outline"
                    className={cn(
                      STATUS_STYLES[selectedAssignment.status]?.bg,
                      "capitalize"
                    )}
                  >
                    {selectedAssignment.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-muted-foreground">Date</p>
                  <p className="text-white font-medium">
                    {formatDate(selectedAssignment.shift?.date)}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Time</p>
                  <p className="text-white font-medium">
                    {selectedAssignment.shift?.start_time} - {selectedAssignment.shift?.end_time}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Duration</p>
                  <p className="text-white font-medium">
                    {selectedAssignment.shift?.duration_hours?.toFixed(1) || "N/A"} hours
                  </p>
                </div>
              </div>

              {/* Indicators */}
              <div className="flex flex-wrap gap-2">
                {isLongShift(selectedAssignment.shift) && (
                  <Badge variant="outline" className="bg-purple-500/10 text-purple-500 border-purple-500/30">
                    <Moon className="h-3 w-3 mr-1" />
                    Long Shift
                  </Badge>
                )}
                {detectOverlaps(selectedAssignment.staff_id).has(selectedAssignment.id) && (
                  <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/30">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Overlap Conflict
                  </Badge>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
